import React, { PropsWithChildren, useCallback, useState } from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import Btn, { BtnModifier } from '../../../components/molecules/Btn';
import { Icons } from '../../../components/atoms/SVG';
import { AcceptDeclineForm } from '../../../_lib/Form2/standardForms/AcceptDeclineForm/AcceptDeclineForm';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useDelayed } from '../../../hooks/useDelayed';
import { useInvalidateRefreshableBlock, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { AdditionalPrivilegeItem, BasicPrivilegeItem } from '../PrivilegeConfig.types';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { BlockType } from '../../../types/api/ElementType';

type PrivilegeContainerProps = PropsWithChildren<{
  privilege: BasicPrivilegeItem | AdditionalPrivilegeItem;
  privilegeLabel: string;
}>;

export function BasicPrivilegeContainer({ privilege, privilegeLabel, children }: PrivilegeContainerProps) {
  const { t } = useSonTranslation('PrivilegeConfig');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { refresh } = useRefreshBlock();
  const closeModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);
  const invalidateRefreshableBlock = useInvalidateRefreshableBlock();

  const refreshAndClose = useCallback(() => {
    refresh();
    // we also want to refresh the additional privileges block.
    invalidateRefreshableBlock(BlockType.additionalprivilegeconfig);
    closeModal();
  }, [closeModal, refresh, invalidateRefreshableBlock]);
  const delayedRefreshAndClose = useDelayed(refreshAndClose);

  return (
    <div className="privilege-config__basic-privilege-container">
      <div className="privilege-config__block">
        <h3 className="privilege-config__block-headline">
          {privilegeLabel}
        </h3>
        <div className="privilege-config__block-buttons">
          <Btn
            svgSpriteId={Icons.DELETE_TRASHCAN}
            modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
            hiddenText
            onClick={() => setShowDeleteModal(true)}
            titleText={t('actions.delete.headline', {
              name: privilegeLabel,
            })}
            text={t('actions.delete.headline', {
              name: privilegeLabel,
            })}
            tooltipPlacement={TooltipPlacement.TOP}
            ariaHaspopup
          />
        </div>
      </div>
      {showDeleteModal
        ? (
            <ModalDialog
              closeModal={() => setShowDeleteModal(false)}
              headline={t('actions.delete.headline', {
                name: privilegeLabel,
              })}
              initialFocusIndex={1}
            >
              <AcceptDeclineForm
                messageSuccess={t('actions.delete.status.success')}
                messageSending={t('actions.delete.status.sending')}
                messageFail={t('actions.delete.status.fail')}
                headline={t('actions.delete.question')}
                form={privilege.delete}
                onSuccess={delayedRefreshAndClose}
              />
            </ModalDialog>
          )
        : null}
      {children}
    </div>
  );
}
